import React, { useState, useEffect } from 'react';
import { AlertCircle, Crown, Calendar, X } from 'lucide-react';

const SubscriptionBanner = ({ user, onUpgradeClick }) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [monetizationEnabled, setMonetizationEnabled] = useState(false);

  useEffect(() => {
    fetchSubscriptionStatus();
    fetchPricing();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/subscription/status`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
      }
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPricing = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/subscription/config`
      );
      if (response.ok) {
        const data = await response.json();
        setPricing(data.pricing);
        setMonetizationEnabled(Boolean(data.monetization_enabled));
      }
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
    }
  };

  if (loading || !subscription || dismissed) {
    return null;
  }

  // Master switch: hide all monetization UI when disabled
  if (!monetizationEnabled) {
    return null;
  }

  // Don't show banner for premium or grandfathered users
  if (subscription.subscription_tier === 'premium' || subscription.subscription_tier === 'grandfathered') {
    return null;
  }

  // Show trial expiry warning
  if (subscription.subscription_tier === 'trial' && subscription.days_remaining !== null) {
    const isUrgent = subscription.days_remaining <= 3;
    
    return (
      <div className={`mb-6 ${isUrgent ? 'bg-gradient-to-r from-orange-50 to-red-50 border-orange-300' : 'bg-brand-bg border-brand-border'} border rounded-lg p-4 shadow-sm`}>
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3 flex-1">
            {isUrgent ? (
              <AlertCircle className="h-6 w-6 text-orange-500 mt-0.5" />
            ) : (
              <Crown className="h-6 w-6 text-brand-accent mt-0.5" />
            )}
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">
                {isUrgent ? '⚠️ Trial Ending Soon!' : '🎉 Premium Trial Active'}
              </h3>
              <p className="text-sm text-gray-700 mt-1">
                {subscription.days_remaining === 0 ? (
                  <span className="font-semibold">Your trial ends today!</span>
                ) : subscription.days_remaining === 1 ? (
                  <span>Your trial ends <span className="font-semibold">tomorrow</span>.</span>
                ) : (
                  <span>You have <span className="font-semibold">{subscription.days_remaining} days</span> left in your Premium trial.</span>
                )}
                {' '}Upgrade now for <strong>{pricing?.monthly?.formatted || '£1.00'}/month</strong> or save {pricing?.annual?.savings_percentage || 33}% with <strong>{pricing?.annual?.formatted || '£10.00'}/year</strong> to keep all the features you&apos;re enjoying!
              </p>
              <button
                onClick={onUpgradeClick}
                className={`mt-3 px-4 py-2 ${isUrgent ? 'bg-orange-500 hover:bg-orange-600' : 'bg-brand-accent hover:bg-brand-accent'} text-white rounded-lg font-medium transition-colors`}
              >
                Upgrade to Premium
              </button>
            </div>
          </div>
          <button
            onClick={() => setDismissed(true)}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
    );
  }

  // Show upgrade prompt for basic users
  if (subscription.subscription_tier === 'basic') {
    return (
      <div className="mb-6 bg-brand-bg border border-brand-border rounded-lg p-4 shadow-sm">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3 flex-1">
            <Crown className="h-6 w-6 text-brand-accent mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">Unlock Premium Features</h3>
              <p className="text-sm text-gray-700 mt-1">
                Get advanced AI insights (monthly, quarterly, yearly), action plan management, and comprehensive analytics for <strong>{pricing?.monthly?.formatted || '£1.00'}/month</strong> or save {pricing?.annual?.savings_percentage || 33}% with <strong>{pricing?.annual?.formatted || '£10.00'}/year</strong>.
              </p>
              <button
                onClick={onUpgradeClick}
                className="mt-3 px-4 py-2 bg-brand-accent hover:bg-brand-accent text-white rounded-lg font-medium transition-colors"
              >
                Upgrade to Premium
              </button>
            </div>
          </div>
          <button
            onClick={() => setDismissed(true)}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default SubscriptionBanner;
